import React, { useState, useEffect } from 'react';
import { useAuth } from '../navigation/AuthProvider';

const EmployeePayVisibility = ({ onClose }) => {
  const { employees: allEmployees } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [compactMode, setCompactMode] = useState(false);

  const PAY_VISIBILITY_OPTIONS = {
    FULL: 'full',
    NONE: 'none',
    BASIC: 'basic'
  };

  const groupedEmployees = {
    basic: employees.filter(emp => emp.payVisibility === PAY_VISIBILITY_OPTIONS.BASIC),
    none: employees.filter(emp => emp.payVisibility === PAY_VISIBILITY_OPTIONS.NONE),
    full: employees.filter(emp => emp.payVisibility === PAY_VISIBILITY_OPTIONS.FULL)
  };

  const getFilteredEmployees = () => {
    let filtered = employees;

    if (activeFilter !== 'all') {
      filtered = groupedEmployees[activeFilter] || [];
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(emp =>
        (emp.name || '').toLowerCase().includes(q) ||
        (emp.email || '').toLowerCase().includes(q) ||
        (emp.role || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  };

  const filteredEmployees = getFilteredEmployees();

  useEffect(() => {
    const loadEmployees = () => {
      if (allEmployees && allEmployees.length > 0) {
        const employeesWithSettings = allEmployees.map(emp => ({
          ...emp,
          payVisibility: PAY_VISIBILITY_OPTIONS.BASIC
        }));
        setEmployees(employeesWithSettings);
      } else {
        setEmployees([]);
      }
      setLoading(false);
    };

    loadEmployees();
  }, [allEmployees]);

  const handleVisibilityChange = (employeeId, visibilityOption) => {
    setEmployees(prev =>
      prev.map(emp =>
        emp._id === employeeId
          ? { ...emp, payVisibility: visibilityOption }
          : emp
      )
    );
  };

  const handleApplyToAll = visibilityOption => {
    setEmployees(prev =>
      prev.map(emp => ({
        ...emp,
        payVisibility: visibilityOption
      }))
    );
    setActiveFilter('all');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      localStorage.setItem(
        'employeePayVisibility',
        JSON.stringify(
          employees.reduce((acc, emp) => {
            acc[emp._id] = emp.payVisibility;
            return acc;
          }, {})
        )
      );

      setSaveMessage('Inställningar sparade!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (e) {
      setSaveMessage('Kunde inte spara inställningar');
    } finally {
      setIsSaving(false);
    }
  };

  const getOptionLabel = option => {
    switch (option) {
      case PAY_VISIBILITY_OPTIONS.FULL:
        return 'Full tillgång';
      case PAY_VISIBILITY_OPTIONS.NONE:
        return 'Ingen tillgång';
      case PAY_VISIBILITY_OPTIONS.BASIC:
        return 'Endast lön';
      default:
        return 'Okänt';
    }
  };

  /* --------------------------- STYLES --------------------------- */

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: 24
    },
    popup: {
      backgroundColor: '#f8fafc',
      borderRadius: 16,
      width: '100%',
      maxWidth: 1200,
      height: '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(15,23,42,0.15)'
    },
    header: {
      padding: '24px 32px 12px 32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    title: {
      margin: 0,
      fontSize: 26,
      fontWeight: 800
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: 26,
      cursor: 'pointer',
      padding: 8,
      borderRadius: 50
    },

    inner: {
      padding: '0 32px 0 32px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      overflow: 'hidden',
      flex: 1
    },

    filtersCard: {
      display: 'flex',
      backgroundColor: '#fff',
      padding: 16,
      borderRadius: 16,
      alignItems: 'center',
      gap: 12,
      boxShadow: '0 1px 3px rgba(15,23,42,0.06)'
    },
    searchInput: {
      flex: 1,
      borderRadius: 12,
      border: '1px solid #e2e8f0',
      padding: '10px 14px',
      fontSize: 14
    },
    select: {
      borderRadius: 12,
      border: '1px solid #e2e8f0',
      padding: '10px 12px',
      fontSize: 14
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 14,
      whiteSpace: 'nowrap'
    },

    tableOuter: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      borderRadius: 16,
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 3px rgba(15,23,42,0.06)'
    },

    tableWrapper: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 14
    },
    th: {
      padding: '14px 24px',
      textAlign: 'left',
      fontWeight: 700,
      borderBottom: '1px dotted #cbd5e1',
      fontSize: 13
    },
    td: {
      padding: '14px 24px',
      borderBottom: '1px solid #f1f5f9',
      fontSize: 14
    },
    row: i => ({
      backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff'
    }),
    nameCell: {
      fontWeight: 500
    },

    visibilitySelect: {
      borderRadius: 999,
      border: '1px solid #e2e8f0',
      padding: '6px 12px',
      fontSize: 13,
      backgroundColor: '#f8fafc'
    },

    footer: {
      padding: '16px 32px 24px 32px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12
    },
    applyButton: {
      borderRadius: 999,
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      padding: '8px 14px',
      cursor: 'pointer',
      fontSize: 13,
      color: '#000' // svart text på knapparna under
    },

    secondaryButton: {
      padding: '10px 18px',
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 999,
      cursor: 'pointer',
      marginRight: 8,
      color: '#000' // också svart text här
    },
    primaryButton: {
      padding: '10px 20px',
backgroundColor: '#007bff',

      color: '#fff',
      borderRadius: 999,
      cursor: 'pointer',
      fontWeight: 600,
      border: 'none'
    },

    loading: {
      padding: 32,
      textAlign: 'center',
      color: '#6b7280'
    },
    saveMessage: success => ({
      marginTop: 8,
      fontSize: 13,
      color: success ? '#15803d' : '#b91c1c'
    })
  };

  /* --------------------------- RENDER --------------------------- */

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.popup} onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Lönesynlighet</h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.inner}>
          
          {/* FILTER BAR */}
          <div style={styles.filtersCard}>
            <input
              type="text"
              placeholder="Sök efter namn, email eller roll..."
              style={styles.searchInput}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />

            <select
              style={styles.select}
              value={activeFilter}
              onChange={e => setActiveFilter(e.target.value)}
            >
              <option value="all">Visa alla</option>
              <option value="basic">Endast lön</option>
              <option value="none">Ingen tillgång</option>
              <option value="full">Full tillgång</option>
            </select>

            <select style={styles.select} disabled>
              <option>December</option>
            </select>
            <select style={styles.select} disabled>
              <option>2025</option>
            </select>

            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={compactMode}
                onChange={() => setCompactMode(prev => !prev)}
              />
              Kompakt läge
            </label>
          </div>

          {/* TABLE (scrollable) */}
          <div style={styles.tableOuter}>
            {loading ? (
              <div style={styles.loading}>Laddar anställda...</div>
            ) : filteredEmployees.length === 0 ? (
              <div style={styles.loading}>
                {searchQuery ? 'Inga anställda matchar sökningen' : 'Inga anställda att visa'}
              </div>
            ) : (
              <table style={styles.tableWrapper}>
                <thead>
                  <tr>
                    <th style={styles.th}>Namn</th>
                    {!compactMode && <th style={styles.th}>E-post</th>}
                    {!compactMode && <th style={styles.th}>Roll</th>}
                    <th style={styles.th}>Synlighet</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEmployees.map((emp, i) => (
                    <tr key={emp._id} style={styles.row(i)}>
                      <td style={{ ...styles.td, ...styles.nameCell }}>
                        {emp.name || 'Okänd användare'}
                      </td>

                      {!compactMode && (
                        <td style={styles.td}>{emp.email || '-'}</td>
                      )}

                      {!compactMode && (
                        <td style={styles.td}>{emp.role || 'Ingen roll'}</td>
                      )}

                      <td style={styles.td}>
                        <select
                          value={emp.payVisibility}
                          onChange={e =>
                            handleVisibilityChange(emp._id, e.target.value)
                          }
                          style={styles.visibilitySelect}
                        >
                          {Object.values(PAY_VISIBILITY_OPTIONS).map(option => (
                            <option key={option} value={option}>
                              {getOptionLabel(option)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

        </div>

        {/* FOOTER */}
        <div style={styles.footer}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={styles.applyButton}
              onClick={() => handleApplyToAll('basic')}
            >
              Alla: Endast lön
            </button>
            <button
              style={styles.applyButton}
              onClick={() => handleApplyToAll('none')}
            >
              Alla: Ingen tillgång
            </button>
            <button
              style={styles.applyButton}
              onClick={() => handleApplyToAll('full')}
            >
              Alla: Full tillgång
            </button>
          </div>

          <div>
            <button style={styles.secondaryButton} onClick={onClose}>
              Avbryt
            </button>

            <button
              style={styles.primaryButton}
              onClick={handleSave}
              disabled={isSaving || loading}
            >
              {isSaving ? 'Sparar…' : 'Spara'}
            </button>

            {saveMessage && (
              <div style={styles.saveMessage(!saveMessage.includes('Kunde inte'))}>
                {saveMessage}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmployeePayVisibility;
